import React, { Component } from "react";
import { Container, Row, Col, Button, Spinner, Modal } from "react-bootstrap";
import SideNavbar from "../navbars/SideNavbar";
import NavbarOne from "../navbars/NavbarOne";
import Footer from "../footer/Footer";
import * as Cookies from "es-cookie";
import { isBrowser } from "react-device-detect";
import CheckboxDropdown from "../common/CheckboxDropdown";
import { uploadInBatches } from "./omrUploadQueue";
import * as XLSX from "xlsx";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import "./_newomr.scss";
import {
  getEffectiveBranchIds,
  onBranchScopeChange,
} from "../../../utils/branchScope";

// Up/down sort carets shown in sortable column headers.
const sortCaret = (order) => {
  const base = { marginLeft: 6, fontSize: 11 };
  if (!order) return <span style={{ ...base, color: "#c7c7d1" }}>↕</span>;
  return (
    <span style={{ ...base, color: "#7c3aed" }}>
      {order === "asc" ? "↑" : "↓"}
    </span>
  );
};

// Compact pagination for the results tables (sortable, searchable via header sort).
// n = the table's own row count, so "All" shows every row instead of a fixed number.
const resultsPagination = (n) =>
  paginationFactory({
    sizePerPage: 25,
    sizePerPageList: [
      { text: "25", value: 25 },
      { text: "50", value: 50 },
      { text: "100", value: 100 },
      { text: "All", value: n || 0 },
    ],
    hidePageListOnlyOnePage: true,
    showTotal: true,
    paginationTotalRenderer: (from, to, size) => (
      <span className="ml-2" style={{ fontSize: 12, color: "#94a3b8" }}>
        {from}–{to} of {size}
      </span>
    ),
  });

// All OMR REST calls (status, review, submit, reread, sheet ops) hit the DEPLOYED college
// backend — that's where the OMR engine + the subject-wise features live. We do NOT honor a
// localhost REACT_APP_API_BASE here: local Node can't auth to the private OMR engine and doesn't
// have the deployed data, so a dev override would just break these calls. Both constants point
// at the deployed backend.
const OMR_API_BASE =
  "https://rizee-college-backend-755214126997.us-south1.run.app";
const OMR_LIVE_BASE = OMR_API_BASE;

// Submit does NOT use the private OMR engine (only DB + student API), so it CAN run
// against a local backend. Dedicated env (separate from REACT_APP_API_BASE so it does
// NOT redirect engine-dependent ops like process-chunk): defaults to the live Cloud Run
// URL (prod unchanged); set REACT_APP_OMR_SUBMIT_BASE=http://localhost:3008 to test local.
const OMR_SUBMIT_BASE =
  process.env.REACT_APP_OMR_SUBMIT_BASE || OMR_LIVE_BASE;

// Live per-exam OMR status dashboard: progress, stat cards, the "needs attention" list
// (only unidentified/low-confidence sheets), missed students, and Submit. Polls /omr/status.
class OmrStatusPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null,
      loading: true,
      err: "",
      view: null, // scan image viewer { row }
      review: null, // review modal { row, loading, data, error }
      results: null, // recorded marks for submitted students
      showResults: true, // results section expanded by default
      busyId: null, // sheet being fixed/re-read
      submitting: false,
      submitMsg: "",
      showMissed: false,
      showMatched: false,
      showAttention: true, // needs-attention accordion, open by default
      addingSheets: false,
      addMsg: "",
      // Add-sheets subject modal (subject-wise papers): pick which subject the new batch is,
      // and hold the picked files until the subject is chosen.
      addSubjectModal: false,
      addSubject: "",
      // Multi-select filters (arrays of ids). Empty array = no filter (show all).
      fBranch: [],
      fClass: [],
      fStream: [],
      fSection: [],
      fSubject: [],
      // Subject-wise papers show Physics/Chemistry/Maths TABS instead of a subject filter.
      // "" = All subjects; otherwise the active subject name.
      activeSubjectTab: "",
      // Carousel verification modal
      carousel: null, // { list: [rows], index }
      carouselReview: null, // review data for the current sheet { loading, data, error }
      carouselBusy: false, // submit/delete in-flight for the current sheet
      carouselMsg: "",
      // Subject-wise papers: the carousel groups by STUDENT (one entry per roll). Each entry
      // holds that student's per-subject sheets; this is the active subject sub-tab within it.
      carStudentSubject: "", // active subject within the current student's grouped review
      carSheetId: null,      // exact active sheet id (distinguishes duplicate same-subject sheets)
      exporting: false, // Excel report download in-flight
      rereadingAll: false, // re-read-all request in-flight
      rereadAllMsg: "", // status text after kicking off a re-read-all
      deletingAll: false, // delete-all request in-flight
      answerEdits: {}, // staged manual answer edits for the current sheet: { "2":"C", ... }
      editingQ: null, // question number currently being edited (inline input)
      copyGroupAnswers: null, // { sheetId: {q:v} } fetched for combined copy (status rows omit answers)
    };
    this.poll = null;
    this.replaceInput = React.createRef(); // hidden file input for replacing one sheet
    this.addInput = React.createRef(); // hidden file input for adding sheets
    this.replaceTargetId = null;
  }

  paperId = () => parseInt(this.props.match?.params?.examPaperId) || 0;

  componentDidMount() {
    if (Cookies.get("token") === undefined) {
      this.props.history.push("/login");
      return;
    }
    this.load();
    this.loadResults(); // show any already-recorded results
    this.poll = setInterval(() => this.load(true), 3000); // live updates while processing
    // Branch comes from the header (NavbarOne) — re-derive the branch filter when
    // it changes. The sheets themselves are already loaded, so this is local only.
    this.offBranchScope = onBranchScopeChange(() =>
      this.setState((s) => ({
        fBranch: this.scopedBranchFilter(s.data && s.data.filters && s.data.filters.branches),
      })),
    );
  }
  componentWillUnmount() {
    if (this.poll) clearInterval(this.poll);
    if (this.offBranchScope) this.offBranchScope();
  }

  // The branch ids this page may show: the header's selection (or every branch the
  // user is authorized for when it selects nothing), intersected with the branches
  // this exam paper actually has sheets for.
  //
  // -1 is a deliberate "match nothing" sentinel: an empty array means "no filter"
  // to pass() below, which would WIDEN back to all branches — the opposite of what
  // an out-of-scope header selection should do.
  scopedBranchFilter = (branchOpts) => {
    const ids = (branchOpts || []).map((o) => o.value);
    const allowed = getEffectiveBranchIds();
    if (!allowed.length) return ids;
    const allow = new Set(allowed.map(String));
    const scoped = ids.filter((v) => allow.has(String(v)));
    return scoped.length ? scoped : [-1];
  };

  load = async (silent) => {
    if (!this.paperId()) return;
    if (!silent) this.setState({ loading: true });
    try {
      const r = await fetch(
        `${OMR_API_BASE}/api/v1/omr/status?exam_paper_id=${this.paperId()}`,
      );
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      // On first load, default-SELECT all filter options (all boxes checked).
      const patch = { data: d, loading: false, err: "" };
      if (!this._filtersInit && d.filters) {
        this._filtersInit = true;
        const ids = (arr) => (arr || []).map((o) => o.value);
        // Branch is not a dropdown on this page — it comes from the header.
        patch.fBranch = this.scopedBranchFilter(d.filters.branches);
        patch.fClass = ids(d.filters.classes);
        patch.fStream = ids(d.filters.streams);
        patch.fSection = ids(d.filters.sections);
      }
      this.setState(patch);
      if (d.done && this.poll) {
        clearInterval(this.poll);
        this.poll = null;
      } // stop polling when done
    } catch (e) {
      this.setState({ err: e.message, loading: false });
    }
  };

  menuToggler = () => {
    const t = Cookies.get("toggle");
    const next = t === "wrapper" ? "wrapper sidebar-enable" : "wrapper";
    Cookies.set("toggle", next);
    this.setState({ toggled: next });
  };

  fixRoll = async (row) => {
    const roll = window.prompt(
      `Correct roll for this sheet (read as: ${row.roll || "none"}):`,
      row.roll || "",
    );
    if (!roll) return;
    this.setState({ busyId: row.id });
    try {
      const r = await fetch(
        `${OMR_LIVE_BASE}/api/v1/omr/sheet/${row.id}/fix-roll`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ roll }),
        },
      );
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      await this.load(true);
    } catch (e) {
      alert("Fix failed: " + e.message);
    }
    this.setState({ busyId: null });
  };

  reread = async (row) => {
    this.setState({ busyId: row.id });
    try {
      const r = await fetch(
        `${OMR_LIVE_BASE}/api/v1/omr/sheet/${row.id}/reread`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        },
      );
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      await this.load(true);
    } catch (e) {
      alert("Re-read failed: " + e.message);
    }
    this.setState({ busyId: null });
  };

  // REVIEW: fetch overlay + detected answers + reasons for ONE sheet, to verify the read.
  openReview = async (row) => {
    this.setState({ review: { row, loading: true } });
    try {
      const r = await fetch(
        `${OMR_LIVE_BASE}/api/v1/omr/sheet/${row.id}/review`,
      );
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      this.setState({ review: { row, loading: false, data: d } });
    } catch (e) {
      this.setState({ review: { row, loading: false, error: e.message } });
    }
  };
  closeReview = () => this.setState({ review: null });

  // Load the recorded RESULTS (marks) for submitted students from the exam engine.
  loadResults = async () => {
    try {
      const r = await fetch(
        `${OMR_LIVE_BASE}/api/v1/omr/results?exam_paper_id=${this.paperId()}`,
      );
      const d = await r.json();
      if (d.status === 200) this.setState({ results: d.results || [] });
    } catch (e) {
      /* non-fatal */
    }
  };

  // Replace ONE sheet's scan with a fresh re-scan (for a bad/unidentified sheet).
  pickReplace = (row) => {
    this.replaceTargetId = row.id;
    if (this.replaceInput.current) this.replaceInput.current.click();
  };
  onReplaceFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file || !this.replaceTargetId) return;
    const id = this.replaceTargetId;
    this.setState({ busyId: id });
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onerror = rej;
        r.onload = () => res(r.result);
        r.readAsDataURL(file);
      });
      const r = await fetch(`${OMR_LIVE_BASE}/api/v1/omr/sheet/${id}/replace`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file: b64 }),
      });
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      await this.load(true);
    } catch (e2) {
      alert("Replace failed: " + e2.message);
    }
    this.setState({ busyId: null });
  };

  // Add MORE sheets to this exam (e.g. for missed students). For a SUBJECT-WISE paper we first
  // ask which subject the new batch is (so the reader tags + offsets them correctly); for a
  // normal paper we go straight to the file picker.
  pickAdd = () => {
    const d = this.state.data || {};
    const isSubjectWise = (d.filters && (d.filters.subjects || []).length > 0) || !!d.paper_template;
    if (isSubjectWise) {
      this.setState({ addSubjectModal: true, addSubject: "" });
    } else if (this.addInput.current) {
      this.addInput.current.click();
    }
  };
  // Engine template for a subject batch, from the paper's template + the chosen subject.
  addTemplateFor = (subject) => {
    const t = this.state.data && this.state.data.paper_template;
    if (t === "jee25") return "jee25";
    if (t === "eapcet80" || t === "eapcet40") return subject === "Maths" ? "eapcet80" : "eapcet40";
    return t || null;   // fall back to the stored paper template
  };
  confirmAddSubject = () => {
    if (!this.state.addSubject) return;
    this.setState({ addSubjectModal: false }, () => {
      if (this.addInput.current) this.addInput.current.click();
    });
  };
  onAddFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const subject = this.state.addSubject || "";
    const template = subject ? this.addTemplateFor(subject) : null;
    this.setState({
      addingSheets: true,
      addMsg: `Uploading 0/${files.length}…`,
    });
    try {
      const apiUrl = `${OMR_API_BASE}/graphql`;   // always the deployed backend for OMR ops
      const { uploaded, failedBatches } = await uploadInBatches(files, {
        apiUrl,
        examPaperId: this.paperId(),
        instituteId: parseInt(Cookies.get("institutionid")),
        subject,      // subject-wise add: tag the new batch (empty for normal papers)
        template,
        onProgress: ({ done, total, batch, totalBatches, phase, attempt }) => {
          const msg =
            phase === "retrying"
              ? `Batch ${batch}/${totalBatches} failed — retry ${attempt}… (${done}/${total})`
              : `Uploading ${done}/${total} (batch ${batch}/${totalBatches})…`;
          this.setState({ addMsg: msg });
        },
      });
      // kick off background reading for whatever uploaded
      fetch(`${OMR_LIVE_BASE}/api/v1/omr/process-chunk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exam_paper_id: this.paperId(), template: template || undefined }),
      }).catch(() => {});
      const failMsg = failedBatches.length
        ? ` · ${failedBatches.length} batch(es) failed — try Add again for the rest`
        : "";
      this.setState({
        addSubject: "",
        addMsg: `Added ${uploaded} ${subject ? subject + " " : ""}sheet(s) — reading…${failMsg}`,
      });
      if (!this.poll) this.poll = setInterval(() => this.load(true), 3000); // resume polling
      await this.load(true);
    } catch (e2) {
      this.setState({ addMsg: "Add failed: " + e2.message });
    }
    this.setState({ addingSheets: false });
  };

  // Submit all matched sheets in SEQUENTIAL BATCHES. A single request for ~1000+ sheets ran the
  // whole engine submit in one HTTP call and the browser aborted it ("Failed to fetch") long
  // before the server finished — leaving a chunk unsubmitted. Batching keeps every request short
  // enough to complete, shows progress, and lets a failed batch be reported without losing the
  // ones that already went. Batches are cut on STUDENT boundaries (never split a student's
  // subjects across two requests) so the by-roll merge into one 75-mark result stays intact.
  // How many matched STUDENTS still have an unsubmitted sheet (for the button label).
  unsubmittedStudentCount = () => {
    const isSubmitted = (s) =>
      s.submitted === true || s.submitted === 1 || s.submitted === "Yes";
    const groups = this.isSubjectWise()
      ? this.groupByStudent(this.allSheets()).filter(
          (g) => g.roll && !g.roll.includes(","),
        )
      : this.allSheets().map((r) => ({ roll: r.roll, sheets: [r] }));
    return groups.filter((g) => g.sheets.some((s) => !isSubmitted(s))).length;
  };

  submit = async () => {
    const STUDENTS_PER_BATCH = 40;
    // Build the same student groups the backend merges by, so each batch is a whole set of
    // students with ALL their subject sheet ids.
    let groups = this.isSubjectWise()
      ? this.groupByStudent(this.allSheets()).filter(
          (g) => g.roll && !g.roll.includes(","),
        )
      : this.allSheets().map((r) => ({ roll: r.roll, sheets: [r] }));
    // SUBMIT ONLY THE UNSUBMITTED. Skip any student whose sheets are ALL already submitted —
    // re-sending them is wasted work (the backend would skip them anyway) and makes the button
    // feel like it "submits everything again". A student stays in if ANY of their sheets is not
    // yet submitted (so a student with new subjects gets re-submitted with the full merged set).
    const isSubmitted = (s) =>
      s.submitted === true || s.submitted === 1 || s.submitted === "Yes";
    const totalStudents = groups.length;
    groups = groups.filter((g) => g.sheets.some((s) => !isSubmitted(s)));
    const skipped = totalStudents - groups.length;
    // only matched (status 2) sheets are eligible; the backend re-checks, but pre-filter so the
    // progress count is honest.
    const batches = [];
    for (let i = 0; i < groups.length; i += STUDENTS_PER_BATCH) {
      const chunk = groups.slice(i, i + STUDENTS_PER_BATCH);
      const ids = chunk.flatMap((g) => g.sheets.map((s) => s.id));
      if (ids.length) batches.push(ids);
    }
    if (!batches.length) {
      this.setState({
        submitMsg:
          skipped > 0
            ? `Nothing new to submit — all ${skipped} student(s) already submitted.`
            : "Nothing to submit.",
      });
      return;
    }
    if (
      !window.confirm(
        `Submit ${groups.length} new/updated student(s) in ${batches.length} batch(es)?` +
          (skipped > 0
            ? `\n(${skipped} already-submitted student(s) will be skipped.)`
            : "") +
          `\nThis records student attempts.`,
      )
    )
      return;

    this.setState({ submitting: true, submitMsg: `Submitting… batch 1/${batches.length}` });
    let totalSubmitted = 0;
    let totalFailed = 0;
    const failedBatches = [];
    for (let b = 0; b < batches.length; b++) {
      this.setState({
        submitMsg: `Submitting… batch ${b + 1}/${batches.length} (${totalSubmitted} done)`,
      });
      try {
        const r = await fetch(`${OMR_SUBMIT_BASE}/api/v1/omr/submit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            exam_paper_id: this.paperId(),
            sheet_ids: batches[b],
          }),
        });
        const d = await r.json();
        if (d.status >= 400) throw new Error(d.message);
        totalSubmitted += d.submitted || 0;
        totalFailed += d.failed || 0;
      } catch (e) {
        // record and keep going — a network hiccup on one batch shouldn't abort the rest.
        failedBatches.push(b + 1);
      }
      // refresh counts between batches so the page reflects progress live
      await this.load(true);
    }
    const msg =
      `Submitted ${totalSubmitted}` +
      (totalFailed ? `, failed ${totalFailed}` : "") +
      (failedBatches.length
        ? `. Batches with errors: ${failedBatches.join(", ")} — click Submit again to retry them.`
        : ".");
    this.setState({ submitting: false, submitMsg: msg });
    await this.load(true);
  };

  // Download a multi-sheet Excel report (Written / Needs Attention / Missed) built
  // from the backend export JSON. Each row carries the original scan filename so
  // staff can find the physical sheet. Uses the frontend's modern xlsx builder.
  downloadExcel = async () => {
    this.setState({ exporting: true });
    try {
      const r = await fetch(
        `${OMR_API_BASE}/api/v1/omr/export?exam_paper_id=${this.paperId()}`,
      );
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      const wb = XLSX.utils.book_new();
      const add = (rows, name, empty) =>
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(
            rows && rows.length ? rows : [{ Note: empty }],
          ),
          name,
        );
      add(d.written, "Written", "No matched sheets");
      add(d.attention, "Needs Attention", "Nothing to fix");
      add(d.missed, "Missed", "No missed students");
      const safe = String(d.exam_name || `exam_${this.paperId()}`)
        .replace(/[^\w-]+/g, "_")
        .slice(0, 50);
      XLSX.writeFile(wb, `OMR_${safe}_${this.paperId()}.xlsx`);
      this.setState({ exporting: false });
    } catch (e) {
      this.setState({ exporting: false });
      alert("Export failed: " + e.message);
    }
  };

  // Re-read every already-read, not-yet-submitted sheet in this paper through the current
  // engine (e.g. after an engine fix). Submitted sheets are skipped by the backend by default
  // so locked marks are never disturbed. Re-reading runs in the background on the server; this
  // just kicks it off and reloads once to reflect the "pending" counts resetting.
  rereadAll = async () => {
    if (
      !window.confirm(
        "Re-read ALL sheets in this paper with the current engine? Already-submitted sheets are skipped.",
      )
    )
      return;
    this.setState({ rereadingAll: true, rereadAllMsg: "" });
    try {
      const r = await fetch(`${OMR_API_BASE}/api/v1/omr/reread-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exam_paper_id: this.paperId() }),
      });
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      this.setState({
        rereadingAll: false,
        rereadAllMsg: `Re-reading ${d.reset} sheet(s)…`,
      });
      await this.load(true);
    } catch (e) {
      this.setState({ rereadingAll: false });
      alert("Re-read all failed: " + e.message);
    }
  };

  // Delete every active, not-yet-submitted sheet in this paper (soft-delete, reversible via
  // estatus like the single-sheet delete). Submitted sheets are skipped by the backend by
  // default so locked/scored results are never touched by this bulk action.
  deleteAll = async () => {
    const confirmText = window.prompt(
      'This deletes ALL sheets in this paper (already-submitted sheets are skipped). Type "DELETE" to confirm.',
    );
    if (confirmText !== "DELETE") return;
    this.setState({ deletingAll: true });
    try {
      const r = await fetch(`${OMR_API_BASE}/api/v1/omr/delete-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exam_paper_id: this.paperId() }),
      });
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      this.setState({ deletingAll: false });
      alert(`Deleted ${d.deleted} sheet(s).`);
      await this.load(true);
    } catch (e) {
      this.setState({ deletingAll: false });
      alert("Delete all failed: " + e.message);
    }
  };

  // Download just the missed students as Excel; filename = ExamName_Section.xlsx.
  downloadMissed = async (missedList) => {
    try {
      // exam name comes from the export endpoint (has exam_name); section from the list.
      let examName = `exam_${this.paperId()}`;
      try {
        const r = await fetch(
          `${OMR_API_BASE}/api/v1/omr/export?exam_paper_id=${this.paperId()}`,
        );
        const d = await r.json();
        if (d && d.exam_name) examName = d.exam_name;
      } catch (e) {}
      const secs = [
        ...new Set(
          (missedList || []).map((m) => m.section_name).filter(Boolean),
        ),
      ];
      const secPart = secs.length ? secs.join("-") : "AllSections";
      const rows = (missedList || []).map((m, i) => ({
        "S.No": i + 1,
        Roll: m.mobile,
        Name: m.name || "",
        Section: m.section_name || "",
        Status: "No sheet uploaded",
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          rows.length ? rows : [{ Note: "No missed students" }],
        ),
        "Missed",
      );
      const safe = (s) =>
        String(s)
          .replace(/[^\w-]+/g, "_")
          .slice(0, 50);
      XLSX.writeFile(wb, `${safe(examName)}_${safe(secPart)}_Missed.xlsx`);
    } catch (e) {
      alert("Download failed: " + e.message);
    }
  };

  // ── Carousel verification modal ─────────────────────────────────────────────
  // Open the carousel over a given list of rows starting at an index, and load the
  // review (overlay + answers) for the first sheet.
  openCarousel = (list, index = 0) => {
    if (!list || !list.length) return;
    this.setState({ carousel: { list, index }, carouselMsg: "" }, () =>
      this.loadCarouselReview(),
    );
  };
  closeCarousel = () => {
    if (this._msgTimer) clearTimeout(this._msgTimer);
    this.setState({ carousel: null, carouselReview: null, carouselMsg: "" });
  };

  // Show a carousel status message that SURVIVES the post-action data reload (load(true)
  // re-renders the modal, which used to make a just-set "✓ Saved" flash disappear). Success
  // messages auto-dismiss; "…in progress" and errors stay until the next action. Because it's
  // held in state and rendered as a fixed toast, the reload can't clear it out from under us.
  flashMsg = (msg, autoDismiss = true) => {
    if (this._msgTimer) clearTimeout(this._msgTimer);
    this.setState({ carouselMsg: msg });
    if (autoDismiss) {
      this._msgTimer = setTimeout(() => {
        // only clear if it's still the same message (don't wipe a newer one)
        this.setState((s) => (s.carouselMsg === msg ? { carouselMsg: "" } : null));
      }, 3500);
    }
  };

  // Is this a subject-wise paper? (has per-subject sheets)
  isSubjectWise = () => {
    const d = this.state.data || {};
    return (d.filters && (d.filters.subjects || []).length > 0) || !!d.paper_template;
  };

  // Flat list of all sheets (needs-attention first, then matched), deduped by id.
  allSheets = () => {
    const d = this.state.data || {};
    const seen = new Set();
    const list = [];
    [...(d.needs_attention || []), ...(d.matched_list || [])].forEach((r) => {
      if (r && r.id != null && !seen.has(r.id)) { seen.add(r.id); list.push(r); }
    });
    return list;
  };

  // Group an arbitrary sheet list by student (roll) — one entry per student with all their
  // subject sheets. Used for the matched table (one row per student) and the verify popup.
  groupByStudent = (sheets) => {
    const byRoll = new Map();
    const singles = [];
    (sheets || []).forEach((r) => {
      const roll = (r.roll || "").trim();
      if (!roll || roll.includes(",")) {
        singles.push({ id: `s${r.id}`, roll, name: r.name, section_name: r.section_name, sheets: [r] });
        return;
      }
      if (!byRoll.has(roll)) {
        byRoll.set(roll, { id: roll, roll, name: r.name, section_name: r.section_name, sheets: [] });
      }
      byRoll.get(roll).sheets.push(r);
    });
    // Derived fields so the table can sort on them (subjects list, sheet count).
    return [...byRoll.values(), ...singles].map((g) => {
      // DUPLICATE-SUBJECT detection: a student sits ONE sheet per subject, so if the same
      // subject appears twice under a roll it means two different students wrote the same roll
      // number (or one sheet's roll was misread onto another student). Flag those subjects so
      // staff can open the group and Fix Roll on the wrong sheet to split them apart.
      const bySubject = {};
      g.sheets.forEach((s) => {
        const sub = s.subject || "?";
        (bySubject[sub] = bySubject[sub] || []).push(s);
      });
      const dupSubjects = Object.keys(bySubject).filter((k) => bySubject[k].length > 1);
      return {
        ...g,
        subjects: g.sheets.map((s) => s.subject).filter(Boolean).sort().join(", "),
        sheetCount: g.sheets.length,
        dupSubjects,                 // e.g. ["Physics","Maths"] when a subject is doubled
        hasDup: dupSubjects.length > 0,
      };
    });
  };

  // SUBJECT-WISE: group the sheets by STUDENT (roll). Each entry = one student with all their
  // subject sheets, so staff reviews a student's Physics/Chemistry/Maths together. Sheets whose
  // roll is blank/unmatched are kept as their own single-sheet groups so they can still be fixed.
  studentGroups = () => this.groupByStudent(this.allSheets());

  // Open the carousel positioned at a specific row. On subject-wise papers this groups by student
  // (so the same student's subjects show together); on normal papers it's the flat per-sheet list.
  openCarouselAtRow = (row) => {
    if (this.isSubjectWise()) {
      const groups = this.studentGroups();
      const gi = groups.findIndex((g) => g.sheets.some((s) => s.id === row.id));
      const g = groups[gi] || groups[0];
      // Open on the sheet that was actually clicked (so a specific duplicate tab lands right).
      const clicked = g && g.sheets.find((s) => s.id === row.id);
      const firstSubject = (clicked && clicked.subject) || (g && g.sheets[0] && g.sheets[0].subject) || "";
      this.setState({ carousel: { groups, index: gi >= 0 ? gi : 0 }, carStudentSubject: firstSubject,
        carSheetId: clicked ? clicked.id : null, carouselMsg: "" }, () => this.loadCarouselReview());
      return;
    }
    const list = this.allSheets();
    const idx = list.findIndex((r) => r.id === row.id);
    this.openCarousel(list, idx >= 0 ? idx : 0);
  };

  carouselGo = (delta) => {
    const { carousel } = this.state;
    if (!carousel) return;
    const len = carousel.groups ? carousel.groups.length : carousel.list.length;
    const next = carousel.index + delta;
    if (next < 0 || next >= len) return;
    // moving to a new student: reset the subject sub-tab to that student's first subject
    let patch = { carousel: { ...carousel, index: next }, carouselMsg: "" };
    if (carousel.groups) {
      const g = carousel.groups[next];
      patch.carStudentSubject = (g && g.sheets[0] && g.sheets[0].subject) || "";
      patch.carSheetId = (g && g.sheets[0] && g.sheets[0].id) || null;
      patch.copyGroupAnswers = null;   // refetch combined answers for the new student
    }
    this.setState(patch, () => this.loadCarouselReview());
  };

  // The sheet currently being reviewed. Grouped mode: the active subject's sheet within the
  // current student. Flat mode: the sheet at the current index.
  currentSheet = () => {
    const { carousel, carStudentSubject, carSheetId } = this.state;
    if (!carousel) return null;
    if (carousel.groups) {
      const g = carousel.groups[carousel.index];
      if (!g) return null;
      // Prefer an explicit sheet id (set it when a student has DUPLICATE subjects, so the two
      // "Physics" tabs point at different sheets). Fall back to first-of-subject, then first.
      if (carSheetId != null) {
        const byId = g.sheets.find((s) => s.id === carSheetId);
        if (byId) return byId;
      }
      return g.sheets.find((s) => s.subject === carStudentSubject) || g.sheets[0] || null;
    }
    return carousel.list[carousel.index];
  };

  // The current student group (grouped mode only).
  currentGroup = () => {
    const { carousel } = this.state;
    return carousel && carousel.groups ? carousel.groups[carousel.index] : null;
  };

  // Switch to another subject within the current student — loads that sheet's review.
  // sheetId targets an EXACT sheet (needed when a student has two of the same subject, so the
  // two "Physics" tabs are distinct); subject stays in sync for the header/label.
  selectStudentSubject = (subject, sheetId) => {
    this.setState(
      { carStudentSubject: subject, carSheetId: sheetId != null ? sheetId : null, carouselMsg: "" },
      () => this.loadCarouselReview(),
    );
  };

  // Open the verify carousel from the top-level "Verify" button. Subject-wise -> grouped by
  // student (verify a student's all subjects together); normal -> flat per-sheet list.
  openVerify = () => {
    if (this.isSubjectWise()) {
      const groups = this.studentGroups();
      if (!groups.length) return;
      const g = groups[0];
      this.setState({ carousel: { groups, index: 0 },
        carStudentSubject: (g.sheets[0] && g.sheets[0].subject) || "",
        carSheetId: (g.sheets[0] && g.sheets[0].id) || null, carouselMsg: "" },
        () => this.loadCarouselReview());
    } else {
      this.openCarousel(this.allSheets(), 0);
    }
  };

  loadCarouselReview = async () => {
    const row = this.currentSheet();
    if (!row) return;
    // reset any staged answer edits when moving to a new sheet
    this.setState({
      carouselReview: { loading: true },
      answerEdits: {},
      editingQ: null,
    });
    try {
      // Overlay + fresh read from the LIVE engine, and the STORED answers (with any
      // manual corrections) from the backend that owns the DB edits. Merge stored
      // answers over the raw read so saved edits persist on reopen.
      const [revRes, ansRes] = await Promise.all([
        fetch(`${OMR_LIVE_BASE}/api/v1/omr/sheet/${row.id}/review`),
        fetch(`${OMR_API_BASE}/api/v1/omr/sheet/${row.id}/answers`).catch(
          () => null,
        ),
      ]);
      const d = await revRes.json();
      if (d.status >= 400) throw new Error(d.message);
      let stored = null;
      try {
        stored = ansRes ? await ansRes.json() : null;
      } catch (e) {
        stored = null;
      }
      if (stored && stored.status === 200 && stored.answers_corrected) {
        // Once corrected, the STORED answers are the complete authoritative set
        // (submit uses exactly these). Replace, don't merge — so cleared questions
        // stay cleared and the raw read doesn't leak back in. Keep the grid's
        // question keys from the read so every box still renders.
        const merged = {};
        Object.keys(d.answers || {}).forEach((q) => {
          merged[q] = stored.answers[q] != null ? stored.answers[q] : "";
        });
        Object.keys(stored.answers).forEach((q) => {
          merged[q] = stored.answers[q];
        });
        d.answers = merged;
        d.counts = { ...(d.counts || {}), answered: stored.answered };
      }
      this.setState({ carouselReview: { loading: false, data: d } });
    } catch (e) {
      this.setState({ carouselReview: { loading: false, error: e.message } });
    }
  };

  // Stage an inline answer edit for question q (value: "A"/"B"/… , numeric, or "" to clear).
  setAnswerEdit = (q, value) =>
    this.setState((s) => ({ answerEdits: { ...s.answerEdits, [q]: value } }));

  // The effective value for a question: staged edit if present, else the engine's read.
  effectiveAnswer = (q) => {
    const { answerEdits, carouselReview } = this.state;
    if (Object.prototype.hasOwnProperty.call(answerEdits, q))
      return answerEdits[q];
    const ans =
      (carouselReview && carouselReview.data && carouselReview.data.answers) ||
      {};
    return ans[q];
  };

  // DISPLAY-ONLY collapse of a raw numeric (Section-II) read into the actual value it represents.
  // Raw shape is 6 space-separated digit-columns: cols 1-4 = integer part, cols 5-6 = exactly 2
  // decimal places (this exam's fixed numeric-answer format — confirmed against the physical
  // sheet: a sign bubble sits between col 4 and col 5, not a decimal point, but the decimal
  // position itself is a fixed convention of this answer format, always 2 places). A column can
  // be "_" (genuinely left blank by the student) or "?" (the engine couldn't read it, a real
  // failure — never silently treated as 0). Never touches the underlying value used for
  // editing/saving, only what's shown in the tile.
  numericDisplay = (raw) => {
    if (typeof raw !== "string" || !raw.includes(" ")) return raw;
    const compact = raw.replace(/\s+/g, "");
    if (compact.includes("?")) return raw; // real read failure — show as-is, never hidden
    if (compact.replace(/_/g, "") === "") return ""; // fully blank — nothing to show
    if (!/^[0-9_]{6}$/.test(compact)) return raw; // not the expected 6-column shape
    const intPart = compact.slice(0, 4).replace(/_/g, "0");
    const decPart = compact.slice(4, 6).replace(/_/g, "0");
    const intVal = String(parseInt(intPart, 10));
    if (decPart === "00") return intVal;
    const dec =
      decPart.endsWith("0") && decPart !== "00" ? decPart.slice(0, 1) : decPart;
    return `${intVal}.${dec}`;
  };

  // Build the plain-text "1<TAB>A\n2<TAB>B" answer list for Excel copy-paste.
  // scope "single" = only the sheet being viewed; "combined" = every subject sheet of this
  // student merged (Physics 1-25, Chemistry 26-50, Maths 51-75) so one paste gives the full row.
  copyText = (scope) => {
    const { carouselReview } = this.state;
    const rows = [];
    if (scope === "single") {
      const ans =
        (carouselReview && carouselReview.data && carouselReview.data.answers) || {};
      Object.keys(ans)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((q) => rows.push([q, this.effectiveAnswer(String(q))]));
    } else {
      // combined: merge every subject sheet's answers (fetched into copyGroupAnswers, since the
      // status rows don't carry answers) plus any live edits on the sheet currently open.
      const { copyGroupAnswers } = this.state;
      const merged = {};
      if (copyGroupAnswers) {
        Object.values(copyGroupAnswers).forEach((a) => {
          Object.keys(a || {}).forEach((q) => {
            merged[q] = a[q];
          });
        });
      }
      const cur =
        (carouselReview && carouselReview.data && carouselReview.data.answers) || {};
      Object.keys(cur).forEach((q) => {
        merged[q] = this.effectiveAnswer(q);
      });
      Object.keys(merged)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((q) => rows.push([q, merged[String(q)]]));
    }
    return rows
      .map(([q, v]) => `${q}\t${v == null || v === "?" ? "" : v}`)
      .join("\n");
  };

  // Fetch stored answers for EVERY sheet in the current student group (status rows carry only
  // the count, not the answers) and cache in copyGroupAnswers, so combined copy has all 3
  // subjects. Returns the map so a copy can use it immediately.
  loadGroupAnswers = async () => {
    const group = this.currentGroup();
    const sheets = group ? group.sheets : [this.currentSheet()].filter(Boolean);
    if (!sheets.length) return {};
    const entries = await Promise.all(
      sheets.map((s) =>
        fetch(`${OMR_API_BASE}/api/v1/omr/sheet/${s.id}/answers`)
          .then((r) => r.json())
          .then((d) => [s.id, (d && d.answers) || {}])
          .catch(() => [s.id, {}]),
      ),
    );
    const map = {};
    entries.forEach(([id, a]) => {
      map[id] = a;
    });
    this.setState({ copyGroupAnswers: map });
    return map;
  };

  // Copy answers to the clipboard in Excel format (question<TAB>answer per line). One click:
  // "single" copies this subject sheet; "combined" fetches all 3 subjects first, then copies 1-75.
  copyAnswers = async (scope) => {
    if (scope === "combined" && !this.state.copyGroupAnswers) {
      await this.loadGroupAnswers();   // populate before building the text
    }
    const text = this.copyText(scope);
    const done = () =>
      this.flashMsg && this.flashMsg("✓ Copied — paste into Excel.");
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, () => this._copyFallback(text));
    } else {
      this._copyFallback(text);
    }
  };

  // Clipboard API can be blocked (insecure origin / permissions). Fall back to a hidden
  // textarea + execCommand so copy still works on http://localhost.
  _copyFallback = (text) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      this.flashMsg && this.flashMsg("✓ Copied — paste into Excel.");
    } catch (e) {
      this.flashMsg && this.flashMsg("Copy failed — select the boxes manually.");
    }
  };

  // Persist all staged answer edits for the current sheet, then reload its review.
  saveAnswers = async () => {
    const row = this.currentSheet();
    const { answerEdits } = this.state;
    if (!row || !Object.keys(answerEdits).length) return;
    this.setState({ carouselBusy: true, carouselMsg: "Saving answers…" });
    try {
      const r = await fetch(
        `${OMR_API_BASE}/api/v1/omr/sheet/${row.id}/fix-answers`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answers: answerEdits }),
        },
      );
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      // reflect the saved answers in the grid WITHOUT reloading /review (which
      // re-reads from the image and would show the raw scan again). Patch the
      // review data's answers with the server's authoritative saved set.
      row.answered = d.answered;
      this.setState((s) => {
        const rev =
          s.carouselReview && s.carouselReview.data
            ? {
                ...s.carouselReview,
                data: {
                  ...s.carouselReview.data,
                  answers: d.answers,
                  counts: {
                    ...(s.carouselReview.data.counts || {}),
                    answered: d.answered,
                  },
                },
              }
            : s.carouselReview;
        return {
          carouselBusy: false,
          carouselMsg: `✓ Answers saved (${d.answered}/${d.total || "?"}).`,
          answerEdits: {},
          editingQ: null,
          carouselReview: rev,
        };
      });
      await this.load(true);
    } catch (e) {
      this.setState({
        carouselBusy: false,
        carouselMsg: "Save failed: " + e.message,
      });
    }
  };

  // Submit just the current sheet to the exam engine.
  submitOne = async () => {
    const row = this.currentSheet();
    if (!row) return;
    // Grouped (subject-wise): submit ALL of this student's subject sheets so the backend merges
    // them by roll into ONE combined result. Flat: just this sheet.
    const group = this.currentGroup();
    const sheetIds = group ? group.sheets.map((s) => s.id) : [row.id];
    this.setState({ carouselBusy: true, carouselMsg: "Submitting…" });
    try {
      const r = await fetch(`${OMR_SUBMIT_BASE}/api/v1/omr/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          exam_paper_id: this.paperId(),
          sheet_ids: sheetIds,
        }),
      });
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      this.setState({ carouselMsg: `✓ Submitted (${d.submitted || 0}).` });
      await this.load(true);
    } catch (e) {
      this.setState({ carouselMsg: "Submit failed: " + e.message });
    }
    this.setState({ carouselBusy: false });
  };

  // Soft-delete the current sheet, drop it from the carousel list, advance.
  deleteOne = async () => {
    const row = this.currentSheet();
    if (!row) return;
    if (
      !window.confirm(
        `Delete this sheet${row.roll ? ` (roll ${row.roll})` : ""}? This removes it from the exam.`,
      )
    )
      return;
    this.setState({ carouselBusy: true, carouselMsg: "Deleting…" });
    try {
      const r = await fetch(
        `${OMR_API_BASE}/api/v1/omr/sheet/${row.id}/delete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        },
      );
      const d = await r.json();
      if (d.status >= 400) throw new Error(d.message);
      const { carousel } = this.state;
      await this.load(true);   // refresh data
      if (carousel.groups) {
        // Grouped (subject-wise): rebuild student groups from the refreshed data, keep the same
        // student position where possible.
        const groups = this.studentGroups();
        if (!groups.length) { this.setState({ carouselBusy: false }); this.closeCarousel(); return; }
        const newIndex = Math.min(carousel.index, groups.length - 1);
        const g = groups[newIndex];
        this.setState({
          carousel: { groups, index: newIndex },
          carStudentSubject: (g && g.sheets[0] && g.sheets[0].subject) || "",
          carSheetId: (g && g.sheets[0] && g.sheets[0].id) || null,
          carouselBusy: false, carouselMsg: "Deleted.",
        }, () => this.loadCarouselReview());
        return;
      }
      // Flat (normal papers): remove the sheet and clamp index.
      const newList = carousel.list.filter((x) => x.id !== row.id);
      if (!newList.length) {
        this.setState({ carouselBusy: false });
        this.closeCarousel();
        return;
      }
      const newIndex = Math.min(carousel.index, newList.length - 1);
      this.setState(
        {
          carousel: { list: newList, index: newIndex },
          carouselBusy: false,
          carouselMsg: "Deleted.",
        },
        () => this.loadCarouselReview(),
      );
    } catch (e) {
      this.setState({
        carouselBusy: false,
        carouselMsg: "Delete failed: " + e.message,
      });
    }
  };

  // ── Sortable-table column definitions for the results lists ──────────────────
  attentionColumns = (hasSubjects) => [
    {
      dataField: "scan",
      text: "Scan",
      sort: false,
      formatter: (c, r) =>
        r.omr_sheet ? (
          <Button
            variant="link"
            size="sm"
            style={{ padding: 0, color: "#7c3aed" }}
            onClick={() => this.openCarouselAtRow(r)}
          >
            <i className="fas fa-search mr-1" />
            Review
          </Button>
        ) : (
          "-"
        ),
    },
    {
      dataField: "scan_name",
      text: "Scan File",
      sort: true,
      formatter: (c) => (
        <span
          style={{ fontFamily: "monospace", fontSize: 12, color: "#475569" }}
        >
          {c || "—"}
        </span>
      ),
    },
    {
      dataField: "roll",
      text: "Read As",
      sort: true,
      formatter: (c) => c || "—",
    },
    {
      dataField: "section_name",
      text: "Section",
      sort: true,
      formatter: (c) => c || "—",
    },
    {
      dataField: "subject",
      text: "Subject",
      sort: true,
      hidden: !hasSubjects,          // only show for subject-wise papers
      formatter: (c) => (c ? (
        <span style={{ fontWeight: 700, color: "#7c3aed", fontSize: 12,
          background: "#f3edfd", borderRadius: 6, padding: "2px 8px" }}>{c}</span>
      ) : "—"),
    },
    {
      dataField: "answered",
      text: "Answered",
      sort: true,
      formatter: (c, r) =>
        r.answered != null ? `${r.answered}/${r.total || "?"}` : "—",
    },
    {
      dataField: "reason",
      text: "Reason",
      sort: true,
      formatter: (c, r) => (
        <span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: r.reason === "unidentified" ? "#dc3545" : "#fd7e14",
            }}
          >
            {r.reason}
          </span>
          {r.reasons && r.reasons.length > 0 && (
            <ul
              style={{
                margin: "4px 0 0",
                paddingLeft: 16,
                fontSize: 12,
                color: "#64748b",
              }}
            >
              {r.reasons.map((rsn, i) => (
                <li key={i}>{rsn}</li>
              ))}
            </ul>
          )}
        </span>
      ),
    },
    {
      dataField: "fix",
      text: "Fix",
      sort: false,
      formatter: (c, r) => (
        <div style={{ whiteSpace: "nowrap" }}>
          <Button
            size="sm"
            disabled={this.state.busyId === r.id}
            style={{
              background: "#7c3aed",
              border: "none",
              color: "#fff",
              fontSize: 12,
              padding: "3px 10px",
            }}
            onClick={() => this.fixRoll(r)}
          >
            Fix Roll
          </Button>{" "}
          <Button
            size="sm"
            disabled={this.state.busyId === r.id}
            style={{
              background: "#fff",
              border: "1px solid #7c3aed",
              color: "#7c3aed",
              fontSize: 12,
              padding: "3px 10px",
            }}
            onClick={() => this.reread(r)}
          >
            {this.state.busyId === r.id ? "…" : "Re-read"}
          </Button>{" "}
          <Button
            size="sm"
            disabled={this.state.busyId === r.id}
            style={{
              background: "#fff",
              border: "1px solid #94a3b8",
              color: "#475569",
              fontSize: 12,
              padding: "3px 10px",
            }}
            onClick={() => this.pickReplace(r)}
          >
            <i className="fas fa-camera mr-1" />
            Re-scan
          </Button>
        </div>
      ),
    },
  ];

  // SUBJECT-WISE matched table: ONE ROW PER STUDENT (grouped by roll), with their subject sheets
  // shown as badges. Clicking Verify opens the grouped popup (subject tabs) for that student.
  matchedStudentColumns = () => [
    { dataField: "name", text: "Student", sort: true, formatter: (c) => c || "—" },
    { dataField: "roll", text: "Roll", sort: true },
    { dataField: "section_name", text: "Section", sort: true, formatter: (c) => c || "—" },
    {
      dataField: "subjects",
      text: "Subjects",
      sort: false,
      formatter: (c, r) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {r.sheets.map((s) => (
            <span key={s.id}
              title={`${s.subject}: ${s.answered}/${s.total} answered`}
              style={{ fontWeight: 700, fontSize: 11.5, borderRadius: 6, padding: "2px 8px",
                color: "#7c3aed", background: "#f3edfd", border: "1px solid #e3d8fb" }}>
              {s.subject || "Sheet"} <span style={{ color: "#9b8ec4", fontWeight: 600 }}>
                {s.answered}/{s.total}
              </span>
            </span>
          ))}
        </div>
      ),
    },
    {
      dataField: "sheetCount",
      text: "Sheets",
      sort: true,
      formatter: (c, r) => (
        <span style={{ fontWeight: 700 }}>{r.sheets.length}</span>
      ),
    },
    {
      dataField: "verify",
      text: "Verify",
      sort: false,
      formatter: (c, r) => (
        <Button variant="link" size="sm"
          style={{ padding: 0, color: "#7c3aed", fontWeight: 600 }}
          title="Verify this student's all subject sheets"
          onClick={() => this.openCarouselAtRow(r.sheets[0])}>
          <i className="fas fa-search mr-1" />
          Verify
        </Button>
      ),
    },
    {
      dataField: "submitted",
      text: "Submitted",
      sort: true,
      formatter: (c, r) =>
        r.sheets.every((s) => s.submitted) ? (
          <span style={{ color: "#16a34a", fontWeight: 600 }}>✓</span>
        ) : (
          <span style={{ color: "#94a3b8" }}>—</span>
        ),
    },
  ];

  matchedColumns = (hasSubjects) => [
    {
      dataField: "name",
      text: "Student",
      sort: true,
      formatter: (c) => c || "—",
    },
    { dataField: "roll", text: "Roll", sort: true },
    {
      dataField: "section_name",
      text: "Section",
      sort: true,
      formatter: (c) => c || "—",
    },
    {
      dataField: "subject",
      text: "Subject",
      sort: true,
      hidden: !hasSubjects,          // only show for subject-wise papers
      formatter: (c) => (c ? (
        <span style={{ fontWeight: 700, color: "#7c3aed", fontSize: 12,
          background: "#f3edfd", borderRadius: 6, padding: "2px 8px" }}>{c}</span>
      ) : "—"),
    },
    {
      dataField: "scan_name",
      text: "Scan File",
      sort: true,
      formatter: (c) => (
        <span
          style={{ fontFamily: "monospace", fontSize: 12, color: "#475569" }}
        >
          {c || "—"}
        </span>
      ),
    },
    {
      dataField: "answered",
      text: "Answered",
      sort: true,
      formatter: (c, r) =>
        r.answered != null ? `${r.answered}/${r.total || "?"}` : "—",
    },
    {
      dataField: "confidence",
      text: "Read",
      sort: true,
      formatter: (c, r) => {
        const rs = r.read_status || "ok";
        const badge =
          rs === "review"
            ? { c: "#b45309", b: "#fef3c7" }
            : { c: "#16a34a", b: "#dcfce7" };
        return (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: badge.c,
              background: badge.b,
              borderRadius: 12,
              padding: "2px 9px",
            }}
          >
            {rs === "review" ? "check" : "ok"}
            {r.confidence != null ? ` ${Math.round(r.confidence * 100)}%` : ""}
          </span>
        );
      },
    },
    {
      dataField: "verify",
      text: "Verify",
      sort: false,
      formatter: (c, r) => (
        <Button
          variant="link"
          size="sm"
          style={{ padding: 0, color: "#7c3aed", fontWeight: 600 }}
          onClick={() => this.openCarouselAtRow(r)}
          title={hasSubjects ? "Verify this student's all subjects" : "Review this sheet"}
        >
          <i className="fas fa-search mr-1" />
          {hasSubjects ? "Verify" : "Review"}
        </Button>
      ),
    },
    {
      dataField: "submitted",
      text: "Submitted",
      sort: true,
      formatter: (c) =>
        c ? (
          <span style={{ color: "#16a34a", fontWeight: 600 }}>✓</span>
        ) : (
          <span style={{ color: "#94a3b8" }}>—</span>
        ),
    },
  ];

  missedColumns = () => [
    { dataField: "sno", text: "#", sort: false, formatter: (c, r, i) => i + 1 },
    {
      dataField: "name",
      text: "Student",
      sort: true,
      formatter: (c, r) => c || r.mobile || "—",
    },
    { dataField: "mobile", text: "Roll", sort: true },
    {
      dataField: "section_name",
      text: "Section",
      sort: true,
      formatter: (c) => c || "—",
    },
  ];

  // Render a results list as a searchable + sortable table (sort carets on every
  // sortable column, a search box, pagination past 25 rows).
  renderResultsTable = (data, columns, keyField) => {
    const cols = columns.map((c) => (c.sort ? { ...c, sortCaret } : c));
    return (
      <ToolkitProvider keyField={keyField} data={data} columns={cols} search>
        {(props) => (
          <div>
            <div style={{ marginBottom: 10 }}>
              <Search.SearchBar
                {...props.searchProps}
                placeholder="Search…"
                style={{
                  height: 38,
                  borderRadius: 8,
                  border: "1.5px solid #e3dbf7",
                  fontSize: 13,
                  minWidth: 220,
                }}
              />
            </div>
            <div style={{ overflowX: "auto" }}>
              <BootstrapTable
                {...props.baseProps}
                pagination={data.length > 25 ? resultsPagination(data.length) : undefined}
                bordered={false}
                hover
                wrapperClasses="table-responsive"
                noDataIndication={() => (
                  <div
                    style={{
                      padding: 20,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    No rows
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </ToolkitProvider>
    );
  };

  render() {
    const { data, loading, err, fBranch, fClass, fStream, fSection } =
      this.state;
    const c = data ? data.counts : {};
    const cov = data ? data.coverage : {};
    const sections = (data && data.sections) || [];
    const filterOpts = (data && data.filters) || {
      branches: [],
      classes: [],
      streams: [],
      sections: [],
      subjects: [],
    };
    const hasSubjects = (filterOpts.subjects || []).length > 0;   // subject-wise paper?
    // fBranch is excluded: it is always populated from the header, so counting it
    // would keep "Clear filters" permanently visible with nothing to clear.
    const anyFilter = fClass.length || fStream.length || fSection.length;

    // A row passes a dimension filter if that filter is empty OR the row's id is selected.
    const pass = (sel, id) =>
      sel.length === 0 || sel.map(Number).includes(Number(id));
    // Subject is chosen via the tabs (activeSubjectTab). "" = All. A row passes if no subject
    // tab is active OR the row's subject matches the active tab.
    const activeSubjectTab = this.state.activeSubjectTab;
    const passSubject = (r) =>
      !activeSubjectTab || r.subject === activeSubjectTab;
    const inFilter = (r) =>
      pass(fBranch, r.branch_id) &&
      pass(fClass, r.class_id) &&
      pass(fStream, r.stream_id) &&
      pass(fSection, r.section_id) &&
      passSubject(r);

    // Error / unidentified sheets have NO section/stream metadata (their roll didn't match a
    // student yet), so a stream/section filter would wrongly HIDE them. They must ALWAYS be
    // shown so staff can find and fix them — bypass the student-metadata filters when the row
    // has no section. The SUBJECT, however, is known at upload even for unidentified sheets, so
    // it STILL respects the subject filter (reviewing "Physics" shouldn't show Chemistry errors).
    const isUnidentified = (r) => r && (r.section_id == null || r.section_id === "");
    const attentionFilter = (r) =>
      passSubject(r) && (isUnidentified(r) || inFilter(r));

    const attention = (data ? data.needs_attention : []).filter(attentionFilter);
    const missedList = (data && data.missed ? data.missed : []).filter(
      inFilter,
    );
    // A sheet can be matched to a roll number before that student's section/branch/stream
    // has synced (or the student record has none), leaving those metadata fields null —
    // same gap the needs-attention list already bypasses above. Without this bypass those
    // matched sheets silently vanish from every branch/section filter, INCLUDING "select
    // all" default, since a null id never equals a real filter id.
    const matchedListAll = (
      data && data.matched_list ? data.matched_list : []
    ).filter((r) => passSubject(r) && (isUnidentified(r) || inFilter(r)));
    // SUBJECT-WISE: the matched table lists ONE ROW PER STUDENT (their subject sheets grouped by
    // roll), so staff sees each student once and verifies all their subjects together.
    const matchedStudents = hasSubjects
      ? this.groupByStudent(matchedListAll)
      : [];
    // Combined list for the carousel: needs-attention first (fix these), then matched.
    // Deduped by id, respects the active filter.
    const verifyList = (() => {
      const seen = new Set();
      const out = [];
      [...attention, ...matchedListAll].forEach((r) => {
        if (r && r.id != null && !seen.has(r.id)) {
          seen.add(r.id);
          out.push(r);
        }
      });
      return out;
    })();
    const totalToRead = c.uploaded || 0;
    const readDone = (c.matched || 0) + (c.unidentified || 0);
    const pct = totalToRead ? Math.round((readDone / totalToRead) * 100) : 0;
    const processing = (c.pending || 0) + (c.processing || 0) > 0;

    const stat = (label, val, accent) => (
      <div
        style={{
          flex: "1 1 130px",
          background: "#fff",
          border: "1px solid #e9ecef",
          borderLeft: `4px solid ${accent}`,
          borderRadius: 8,
          padding: "14px 16px",
        }}
      >
        <div
          style={{
            fontSize: 26,
            fontWeight: 800,
            color: "#7c3aed",
            lineHeight: 1,
          }}
        >
          {val ?? 0}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#6c757d",
            marginTop: 4,
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          {label}
        </div>
      </div>
    );

    return (
      <div className={Cookies.get("toggle")}>
        <div className="header-area">
          <NavbarOne onClick={this.menuToggler} />
        </div>
        <div className="main-wrapper">
          <div
            id="sidebar"
            className={
              isBrowser
                ? "sidebar-menu d-none d-xl-block d-lg-block"
                : "sidebar-menu d-block d-xl-none d-lg-none"
            }
          >
            <SideNavbar onClick={this.menuToggler} />
          </div>
          <div className="student-overlay" onClick={this.menuToggler} />
          <div className="content-wrapper pt-0">
            <Container
              fluid
              style={{
                padding: "20px 24px 60px",
                background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)",
                minHeight: "80vh",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <Button
                  variant="link"
                  style={{ color: "#7c3aed", fontWeight: 600, padding: 0 }}
                  onClick={() =>
                    this.props.history.push("/offline_exams/omr/history")
                  }
                >
                  <i className="fas fa-arrow-left mr-2" />
                  Back to History
                </Button>
                <h4 style={{ margin: 0, color: "#7c3aed", fontWeight: 800 }}>
                  OMR Results
                </h4>
              </div>

              {loading && !data ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "50vh",
                    gap: 14,
                  }}
                >
                  <Spinner
                    animation="border"
                    style={{ color: "#7c3aed", width: 44, height: 44 }}
                  />
                  <div style={{ color: "#7c3aed", fontWeight: 600 }}>
                    Loading OMR results…
                  </div>
                </div>
              ) : err ? (
                <div
                  style={{
                    background: "#fff5f5",
                    border: "1px solid #fecaca",
                    borderRadius: 10,
                    padding: "16px 20px",
                    color: "#dc2626",
                    fontWeight: 600,
                  }}
                >
                  <i className="fas fa-exclamation-triangle mr-2" />
                  Could not load status: {err}
                </div>
              ) : (
                <>
                  {/* progress */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e9ecef",
                      borderRadius: 10,
                      padding: "16px 20px",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 8,
                      }}
                    >
                      <span style={{ fontWeight: 700, color: "#7c3aed" }}>
                        {processing ? (
                          <>
                            <Spinner
                              size="sm"
                              animation="border"
                              className="mr-2"
                            />
                            Reading sheets…
                          </>
                        ) : (
                          "Reading complete"
                        )}
                      </span>
                      <span style={{ color: "#6c757d" }}>
                        {readDone} / {totalToRead} read
                      </span>
                    </div>
                    <div
                      style={{
                        height: 10,
                        background: "#eef1f5",
                        borderRadius: 6,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${pct}%`,
                          background: "#7c3aed",
                          transition: "width .4s",
                        }}
                      />
                    </div>
                  </div>

                  {/* stat cards */}
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      flexWrap: "wrap",
                      marginBottom: 16,
                    }}
                  >
                    {stat("Matched", c.matched, "#198754")}
                    {stat("Needs fix", attention.length, "#dc3545")}
                    {stat("Missed", cov.missed_count, "#fd7e14")}
                    {stat(
                      "Pending",
                      (c.pending || 0) + (c.processing || 0),
                      "#6c757d",
                    )}
                    {stat("Expected", cov.expected, "#7c3aed")}
                  </div>

                  {/* Subject TABS — only for subject-wise papers. Click a subject to view just
                      its sheets (Physics / Chemistry / Maths); "All" shows everything. */}
                  {hasSubjects && (
                    <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap",
                      borderBottom: "2px solid #ececf3", paddingBottom: 0 }}>
                      {[{ value: "", label: "All Subjects" },
                        ...(filterOpts.subjects || [])].map((t) => {
                        const on = this.state.activeSubjectTab === t.value;
                        // count sheets for this subject (matched + attention)
                        const all = [...(data.matched_list || []), ...(data.needs_attention || [])];
                        const count = t.value
                          ? all.filter((r) => r.subject === t.value).length
                          : all.length;
                        return (
                          <button key={t.value || "all"} type="button"
                            onClick={() => this.setState({ activeSubjectTab: t.value })}
                            style={{
                              padding: "10px 20px", border: "none", cursor: "pointer",
                              background: "transparent", fontSize: 14,
                              fontWeight: on ? 800 : 600,
                              color: on ? "#7c3aed" : "#6c757d",
                              borderBottom: on ? "3px solid #7c3aed" : "3px solid transparent",
                              marginBottom: -2 }}>
                            {t.label}
                            <span style={{ marginLeft: 7, fontSize: 12, fontWeight: 700,
                              background: on ? "#f3edfd" : "#eef1f5",
                              color: on ? "#7c3aed" : "#8a94a6",
                              borderRadius: 10, padding: "1px 8px" }}>{count}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* filter bar — Class / Stream / Section. No Branch filter: the
                      header's branch selection scopes this page. */}
                  {(filterOpts.classes.length > 0 ||
                    filterOpts.streams.length > 0 ||
                    filterOpts.sections.length > 0) && (
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e9ecef",
                        borderRadius: 10,
                        padding: "16px 18px",
                        marginBottom: 16,
                      }}
                    >
                      <Row className="g-2">
                        {[
                          {
                            key: "fClass",
                            label: "Class",
                            opts: filterOpts.classes,
                            ph: "All Classes",
                          },
                          {
                            key: "fStream",
                            label: "Stream",
                            opts: filterOpts.streams,
                            ph: "All Streams",
                          },
                          {
                            key: "fSection",
                            label: "Section",
                            opts: filterOpts.sections,
                            ph: "All Sections",
                          },
                        ]
                          .filter((f) => f.opts.length > 0)
                          .map((f) => (
                            <Col
                              key={f.key}
                              lg
                              md={6}
                              sm={12}
                              className="mb-2 mb-lg-0"
                            >
                              <label
                                style={{
                                  display: "block",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#6B7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.7px",
                                  marginBottom: 6,
                                }}
                              >
                                {f.label}
                              </label>
                              <CheckboxDropdown
                                options={f.opts}
                                value={this.state[f.key]}
                                onChange={(val) =>
                                  this.setState({ [f.key]: val })
                                }
                                placeholder={f.ph}
                              />
                            </Col>
                          ))}
                      </Row>
                      {anyFilter ? (
                        <div style={{ marginTop: 10 }}>
                          <Button
                            variant="link"
                            style={{
                              padding: 0,
                              color: "#7c3aed",
                              fontWeight: 600,
                              fontSize: 13,
                            }}
                            onClick={() =>
                              this.setState({
                                // fBranch is NOT cleared to [] — that means "no
                                // filter" and would widen past the header's scope.
                                fBranch: this.scopedBranchFilter(filterOpts.branches),
                                fClass: [],
                                fStream: [],
                                fSection: [],
                              })
                            }
                          >
                            Clear filters
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* per-section status — which sections are done vs pending (click to filter) */}
                  {sections.length > 0 && (
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e9ecef",
                        borderRadius: 10,
                        padding: "14px 18px",
                        marginBottom: 16,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#7c3aed",
                          marginBottom: 10,
                        }}
                      >
                        Sections
                      </div>
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 10 }}
                      >
                        {sections.map((s) => {
                          const complete =
                            s.missed === 0 &&
                            s.needs_fix === 0 &&
                            s.expected > 0;
                          const active = fSection
                            .map(Number)
                            .includes(Number(s.section_id));
                          return (
                            <button
                              key={s.section_id}
                              onClick={() =>
                                this.setState({
                                  fSection: active ? [] : [s.section_id],
                                })
                              }
                              style={{
                                textAlign: "left",
                                cursor: "pointer",
                                border: `1.5px solid ${active ? "#7c3aed" : complete ? "#bbf7d0" : "#fed7aa"}`,
                                background: active
                                  ? "#f5f0ff"
                                  : complete
                                    ? "#f0fdf4"
                                    : "#fff7ed",
                                borderRadius: 8,
                                padding: "8px 12px",
                                minWidth: 150,
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 700,
                                  color: "#0f172a",
                                  fontSize: 13,
                                  marginBottom: 2,
                                }}
                              >
                                {s.section_name}{" "}
                                {complete ? (
                                  <span style={{ color: "#16a34a" }}>✓</span>
                                ) : (
                                  <span
                                    style={{
                                      color: "#ea580c",
                                      fontSize: 11,
                                      fontWeight: 700,
                                    }}
                                  >
                                    PENDING
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: "#475569" }}>
                                {s.matched}/{s.expected} uploaded
                                {s.missed > 0 && (
                                  <span style={{ color: "#c2410c" }}>
                                    {" "}
                                    · {s.missed} missing
                                  </span>
                                )}
                                {s.needs_fix > 0 && (
                                  <span style={{ color: "#dc2626" }}>
                                    {" "}
                                    · {s.needs_fix} to fix
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* hidden file inputs for replace-one / add-many */}
                  <input
                    ref={this.replaceInput}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={this.onReplaceFile}
                  />
                  <input
                    ref={this.addInput}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={this.onAddFiles}
                  />

                  {/* actions: add sheets + submit */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e9ecef",
                      borderRadius: 10,
                      padding: "14px 18px",
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      className="omr-btn"
                      style={{
                        marginTop: 0,
                        background: "#fff",
                        border: "1.5px solid #7c3aed",
                        color: "#7c3aed",
                      }}
                      disabled={this.state.addingSheets}
                      onClick={this.pickAdd}
                    >
                      {this.state.addingSheets ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="mr-2"
                          />
                          Adding…
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus mr-2" />
                          Add Sheets
                        </>
                      )}
                    </button>
                    {this.state.addMsg && (
                      <span
                        style={{
                          color: "#7c3aed",
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        {this.state.addMsg}
                      </span>
                    )}
                    <button
                      className="omr-btn"
                      style={{
                        marginTop: 0,
                        background: "#fff",
                        border: "1.5px solid #7c3aed",
                        color: "#7c3aed",
                        opacity: verifyList.length ? 1 : 0.5,
                      }}
                      disabled={!verifyList.length}
                      onClick={this.openVerify}
                    >
                      <i className="fas fa-images mr-2" />
                      {hasSubjects
                        ? `Verify Students (${this.studentGroups().length})`
                        : `Verify Sheets (${verifyList.length})`}
                    </button>
                    <button
                      className="omr-btn"
                      style={{
                        marginTop: 0,
                        background: "#fff",
                        border: "1.5px solid #16a34a",
                        color: "#16a34a",
                      }}
                      disabled={this.state.exporting}
                      onClick={this.downloadExcel}
                    >
                      {this.state.exporting ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="mr-2"
                          />
                          Preparing…
                        </>
                      ) : (
                        <>
                          <i className="fas fa-file-excel mr-2" />
                          Download Excel
                        </>
                      )}
                    </button>
                    <button
                      className="omr-btn"
                      style={{
                        marginTop: 0,
                        background: "#fff",
                        border: "1.5px solid #7c3aed",
                        color: "#7c3aed",
                      }}
                      disabled={this.state.rereadingAll}
                      onClick={this.rereadAll}
                    >
                      {this.state.rereadingAll ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="mr-2"
                          />
                          Starting…
                        </>
                      ) : (
                        <>
                          <i className="fas fa-redo mr-2" />
                          Re-read All
                        </>
                      )}
                    </button>
                    {this.state.rereadAllMsg && (
                      <span
                        style={{
                          color: "#7c3aed",
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        {this.state.rereadAllMsg}
                      </span>
                    )}
                    <button
                      className="omr-btn"
                      style={{
                        marginTop: 0,
                        background: "#fff",
                        border: "1.5px solid #dc2626",
                        color: "#dc2626",
                      }}
                      disabled={this.state.deletingAll}
                      onClick={this.deleteAll}
                    >
                      {this.state.deletingAll ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="mr-2"
                          />
                          Deleting…
                        </>
                      ) : (
                        <>
                          <i className="fas fa-trash mr-2" />
                          Delete All
                        </>
                      )}
                    </button>
                    <span style={{ flex: 1 }} />
                    <span style={{ color: "#6c757d", fontSize: 13 }}>
                      When the reads look right:
                    </span>
                    <button
                      className="omr-btn active"
                      style={{
                        marginTop: 0,
                        opacity:
                          this.state.submitting ||
                          this.unsubmittedStudentCount() === 0
                            ? 0.5
                            : 1,
                      }}
                      disabled={
                        this.state.submitting ||
                        this.unsubmittedStudentCount() === 0
                      }
                      onClick={this.submit}
                    >
                      {this.state.submitting
                        ? "Submitting…"
                        : (() => {
                            const n = this.unsubmittedStudentCount();
                            return n > 0
                              ? `Submit ${n} New`
                              : "All Submitted ✓";
                          })()}
                    </button>
                    {this.state.submitMsg && (
                      <span
                        style={{
                          color: "#198754",
                          fontWeight: 600,
                          width: "100%",
                        }}
                      >
                        {this.state.submitMsg}
                      </span>
                    )}
                  </div>

                  {/* needs attention */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e9ecef",
                      borderRadius: 10,
                      padding: "16px 20px",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      onClick={() =>
                        this.setState({
                          showAttention: !this.state.showAttention,
                        })
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        fontWeight: 700,
                        color: "#7c3aed",
                        marginBottom: this.state.showAttention ? 10 : 0,
                      }}
                    >
                      <span>
                        Needs attention{" "}
                        {attention.length ? `(${attention.length})` : ""}
                      </span>
                      <i
                        className={`fas fa-chevron-${this.state.showAttention ? "up" : "down"}`}
                      />
                    </div>
                    {this.state.showAttention &&
                      (attention.length === 0 ? (
                        <div style={{ color: "#198754", fontWeight: 600 }}>
                          <i className="fas fa-check-circle mr-2" />
                          All sheets matched — nothing to fix.
                        </div>
                      ) : (
                        this.renderResultsTable(
                          attention,
                          this.attentionColumns(hasSubjects),
                          "id",
                        )
                      ))}
                  </div>

                  {/* missed students (collapsible) */}
                  {missedList.length > 0 && (
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e9ecef",
                        borderRadius: 10,
                        padding: "16px 20px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <Button
                          variant="link"
                          style={{
                            padding: 0,
                            color: "#7c3aed",
                            fontWeight: 700,
                          }}
                          onClick={() =>
                            this.setState({
                              showMissed: !this.state.showMissed,
                            })
                          }
                        >
                          Missed Students ({missedList.length})
                          {anyFilter ? " · filtered" : ""}{" "}
                          <i
                            className={`fas fa-chevron-${this.state.showMissed ? "up" : "down"} ml-1`}
                          />
                        </Button>
                        <Button
                          size="sm"
                          style={{
                            background: "#fff",
                            border: "1.5px solid #ea580c",
                            color: "#ea580c",
                            fontWeight: 600,
                          }}
                          onClick={() => this.downloadMissed(missedList)}
                        >
                          <i className="fas fa-file-excel mr-1" />
                          Download Missed
                        </Button>
                      </div>
                      {this.state.showMissed && (
                        <div style={{ marginTop: 10 }}>
                          {this.renderResultsTable(
                            missedList.map((m, i) => ({
                              ...m,
                              mobile: m.mobile || `row-${i}`,
                            })),
                            this.missedColumns(),
                            "mobile",
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* matched results (collapsible full table) */}
                  {matchedListAll.length > 0 && (
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e9ecef",
                        borderRadius: 10,
                        padding: "16px 20px",
                        marginTop: 16,
                      }}
                    >
                      <Button
                        variant="link"
                        style={{
                          padding: 0,
                          color: "#7c3aed",
                          fontWeight: 700,
                        }}
                        onClick={() =>
                          this.setState({
                            showMatched: !this.state.showMatched,
                          })
                        }
                      >
                        {hasSubjects
                          ? `Matched students (${matchedStudents.length})`
                          : `Matched results (${matchedListAll.length})`}
                        {anyFilter ? " · filtered" : ""}{" "}
                        <i
                          className={`fas fa-chevron-${this.state.showMatched ? "up" : "down"} ml-1`}
                        />
                      </Button>
                      {this.state.showMatched && (
                        <div style={{ marginTop: 10 }}>
                          {/* Subject-wise: ONE ROW PER STUDENT (their subject sheets as badges).
                              Normal papers: the usual one-row-per-sheet table. */}
                          {hasSubjects
                            ? this.renderResultsTable(
                                matchedStudents,
                                this.matchedStudentColumns(),
                                "id",
                              )
                            : this.renderResultsTable(
                                matchedListAll,
                                this.matchedColumns(hasSubjects),
                                "id",
                              )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </Container>
          </div>
          <Footer />
        </div>

        {/* scan viewer */}
        <Modal
          show={!!this.state.view}
          onHide={() => this.setState({ view: null })}
          size="lg"
          centered
        >
          <Modal.Header closeButton style={{ background: "#7c3aed" }}>
            <Modal.Title style={{ color: "#fff", fontSize: 16 }}>
              Scanned sheet — {this.state.view?.row?.roll || ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ textAlign: "center", background: "#f8f9fa" }}>
            {this.state.view?.row?.omr_sheet ? (
              <img
                alt="scan"
                src={this.state.view.row.omr_sheet}
                style={{
                  maxWidth: "100%",
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                }}
              />
            ) : (
              "No image."
            )}
          </Modal.Body>
        </Modal>

        {/* REVIEW modal — overlay (how the engine read it) + answers grid + reasons */}
        <Modal
          show={!!this.state.review}
          onHide={this.closeReview}
          size="xl"
          scrollable
        >
          <Modal.Header closeButton style={{ background: "#7c3aed" }}>
            <Modal.Title style={{ color: "#fff", fontSize: 16 }}>
              Review read —{" "}
              {this.state.review?.data?.roll ||
                this.state.review?.row?.roll ||
                ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.state.review?.loading ? (
              <div style={{ textAlign: "center", padding: 30 }}>
                <Spinner animation="border" /> Generating overlay…
              </div>
            ) : this.state.review?.error ? (
              <div className="text-danger">
                Could not load review: {this.state.review.error}
              </div>
            ) : this.state.review?.data ? (
              <>
                {/* reasons banner */}
                {this.state.review.data.reasons &&
                  this.state.review.data.reasons.length > 0 && (
                    <div
                      style={{
                        background: "#fff7ed",
                        border: "1px solid #fed7aa",
                        borderRadius: 8,
                        padding: "10px 14px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#9a3412",
                          marginBottom: 4,
                        }}
                      >
                        <i className="fas fa-exclamation-triangle mr-2" />
                        Why this needs a check:
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: 18,
                          color: "#7c2d12",
                          fontSize: 13,
                        }}
                      >
                        {this.state.review.data.reasons.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                <Row>
                  <Col md={6}>
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#7c3aed",
                        marginBottom: 6,
                      }}
                    >
                      How the engine read it
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#94a3b8",
                        marginBottom: 6,
                      }}
                    >
                      green = detected · red = flagged/ambiguous
                    </div>
                    {this.state.review.data.overlay_image ? (
                      <img
                        alt="overlay"
                        src={`data:image/jpeg;base64,${this.state.review.data.overlay_image}`}
                        style={{
                          maxWidth: "100%",
                          border: "1px solid #dee2e6",
                          borderRadius: 6,
                        }}
                      />
                    ) : (
                      <div style={{ color: "#dc3545" }}>
                        Overlay unavailable
                      </div>
                    )}
                  </Col>
                  <Col md={6}>
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#7c3aed",
                        marginBottom: 6,
                      }}
                    >
                      Detected answers ·{" "}
                      {this.state.review.data.counts?.answered ?? "?"}/
                      {this.state.review.data.counts?.total ?? "?"} · conf{" "}
                      {this.state.review.data.confidence}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill,minmax(52px,1fr))",
                        gap: 3,
                      }}
                    >
                      {Object.keys(this.state.review.data.answers || {}).map(
                        (q) => {
                          const raw = this.state.review.data.answers[q];
                          // Numeric (Section-II) answers come back as one char per digit-
                          // column, space-separated (e.g. "0 0 0 6 0 0"), and a fully-unread
                          // question as all-underscores (e.g. "_ _ _ _ _ _"). Blank = un-
                          // answered, so show nothing for it rather than a run of dashes —
                          // and strip the spaces for a real number so it reads as one compact
                          // value instead of wrapping mid-number in the narrow tile. A real
                          // value is further collapsed to the actual number it represents
                          // (e.g. "0 0 1 2 _ _" -> "12") via numericDisplay — see its own comment.
                          const isBlankNumeric =
                            typeof raw === "string" &&
                            raw.includes(" ") &&
                            raw.replace(/[\s_]/g, "") === "";
                          const v = isBlankNumeric ? "" : raw;
                          const display = this.numericDisplay(v);
                          const bg =
                            v === "?" ? "#fdecec" : v ? "#eafaef" : "#f5f5f5";
                          const col =
                            v === "?" ? "#c00" : v ? "#127a35" : "#bbb";
                          return (
                            <div
                              key={q}
                              style={{
                                border: "1px solid #e0e0e6",
                                borderRadius: 5,
                                padding: "2px 3px",
                                textAlign: "center",
                                background: bg,
                              }}
                            >
                              <div style={{ fontSize: 9, color: "#888" }}>
                                {q}
                              </div>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: 12,
                                  color: col,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {v === "?" ? "?" : display || "·"}
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </Col>
                </Row>
              </>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            {this.state.review?.row && (
              <>
                <Button
                  size="sm"
                  style={{ background: "#7c3aed", border: "none" }}
                  onClick={() => {
                    const row = this.state.review.row;
                    this.closeReview();
                    this.fixRoll(row);
                  }}
                >
                  Fix Roll
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    const row = this.state.review.row;
                    this.closeReview();
                    this.pickReplace(row);
                  }}
                >
                  <i className="fas fa-camera mr-1" />
                  Re-scan
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>

        {/* CAROUSEL verification modal — page through sheets, submit/delete each */}
        {(() => {
          const car = this.state.carousel;
          if (!car) return null;
          const grouped = !!car.groups;              // subject-wise: grouped by student
          const total = grouped ? car.groups.length : car.list.length;
          const group = grouped ? car.groups[car.index] : null;
          const row = this.currentSheet();           // active sheet (subject within student, or flat)
          const rev = this.state.carouselReview;
          const busy = this.state.carouselBusy;
          const atStart = car.index === 0;
          const atEnd = car.index === total - 1;
          return (
            <Modal show onHide={this.closeCarousel} size="xl" scrollable>
              <Modal.Header closeButton style={{ background: "#7c3aed" }}>
                <Modal.Title style={{ color: "#fff", fontSize: 16 }}>
                  {grouped ? "Verify student" : "Verify sheets"} — {car.index + 1} / {total}
                  {grouped
                    ? (group && group.roll ? ` · roll ${group.roll}` : "")
                    : (row && row.roll ? ` · roll ${row.roll}` : "")}
                  {row && row.section_name ? ` · ${row.section_name}` : ""}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {/* Subject sub-tabs — one student's Physics/Chemistry/Maths sheets. Click to
                    view + verify that subject's scan & answers. */}
                {grouped && group && (() => {
                  // Which subjects appear more than once under this roll? Two sheets of the same
                  // subject means two DIFFERENT students share this roll (a misread), so mark
                  // those tabs red — staff opens the wrong one and Fix Roll splits them apart.
                  const subjCount = {};
                  group.sheets.forEach((s) => {
                    const k = s.subject || "?";
                    subjCount[k] = (subjCount[k] || 0) + 1;
                  });
                  const hasDup = Object.values(subjCount).some((n) => n > 1);
                  return (
                    <>
                      {hasDup && (
                        <div style={{ marginBottom: 8, padding: "6px 12px", borderRadius: 6,
                          background: "#fff3f3", color: "#b91c1c", fontSize: 12.5, fontWeight: 600 }}>
                          ⚠ Duplicate subject sheets under roll {group.roll} — two students likely
                          share this roll. Open the wrong sheet below and use <b>Fix Roll</b> to
                          re-assign it.
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap",
                        borderBottom: "2px solid #ececf3" }}>
                        {group.sheets.map((s) => {
                          const on = row && s.id === row.id;
                          const dup = (subjCount[s.subject || "?"] || 0) > 1;
                          const scan = s.scan_name || s.file_name || ("#" + s.id);
                          return (
                            <button key={s.id} type="button"
                              onClick={() => this.selectStudentSubject(s.subject, s.id)}
                              title={scan}
                              style={{ padding: "8px 16px", border: "none", cursor: "pointer",
                                background: dup ? "#fff3f3" : "transparent", borderRadius: "6px 6px 0 0",
                                fontSize: 13,
                                fontWeight: on ? 800 : 600,
                                color: on ? "#7c3aed" : (dup ? "#b91c1c" : "#6c757d"),
                                borderBottom: on ? "3px solid #7c3aed" : "3px solid transparent",
                                marginBottom: -2, display: "flex", flexDirection: "column",
                                alignItems: "flex-start", lineHeight: 1.25 }}>
                              <span>
                                {s.subject || "Sheet"}
                                {dup && <span style={{ marginLeft: 5, fontSize: 11 }}>⚠</span>}
                                {s.reason === "unidentified" && (
                                  <span style={{ marginLeft: 6, color: "#dc3545", fontSize: 11 }}>!</span>
                                )}
                              </span>
                              <span style={{ fontSize: 10.5, fontWeight: 500, opacity: 0.8 }}>{scan}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
                {/* sheet meta */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 700, color: "#0f172a" }}>
                    {row && row.name ? row.name : (row && row.roll) || "—"}
                  </span>
                  {row && row.reason && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color:
                          row.reason === "unidentified" ? "#dc3545" : "#fd7e14",
                      }}
                    >
                      {row.reason}
                    </span>
                  )}
                  {row && row.answered != null && (
                    <span style={{ fontSize: 12, color: "#64748b" }}>
                      answered {row.answered}/{row.total || "?"}
                    </span>
                  )}
                  {row && row.submitted && (
                    <span
                      style={{
                        color: "#16a34a",
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      ✓ submitted
                    </span>
                  )}
                </div>

                {rev && rev.loading ? (
                  <div style={{ textAlign: "center", padding: 30 }}>
                    <Spinner animation="border" /> Loading sheet…
                  </div>
                ) : rev && rev.error ? (
                  <div className="text-danger">
                    Could not load review: {rev.error}
                  </div>
                ) : rev && rev.data ? (
                  <Row>
                    <Col md={6}>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#7c3aed",
                          marginBottom: 6,
                        }}
                      >
                        How the engine read it
                      </div>
                      {/* Scan scrolls INSIDE its own box so the tall OMR image and the answers
                          grid scroll independently — reviewer can keep answers in view while
                          scrolling down the scan. */}
                      <div
                        style={{
                          maxHeight: "70vh",
                          overflowY: "auto",
                          border: "1px solid #dee2e6",
                          borderRadius: 6,
                        }}
                      >
                        {rev.data.overlay_image ? (
                          <img
                            alt="overlay"
                            src={`data:image/jpeg;base64,${rev.data.overlay_image}`}
                            style={{ maxWidth: "100%", display: "block" }}
                          />
                        ) : row && row.omr_sheet ? (
                          <img
                            alt="scan"
                            src={row.omr_sheet}
                            style={{ maxWidth: "100%", display: "block" }}
                          />
                        ) : (
                          <div style={{ color: "#dc3545", padding: 12 }}>
                            No image
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col
                      md={6}
                      style={{ maxHeight: "70vh", overflowY: "auto" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          marginBottom: 6,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontWeight: 700, color: "#7c3aed" }}>
                          Detected answers · {rev.data.counts?.answered ?? "?"}/
                          {rev.data.counts?.total ?? "?"} · conf{" "}
                          {rev.data.confidence}
                        </span>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>
                          click a box to edit
                        </span>
                        {/* One-click copy to clipboard in Excel format (question<TAB>answer per
                            line). "This" = this subject sheet; "All 75" = the student's 3 subjects
                            merged. Paste straight into Excel. */}
                        <button
                          type="button"
                          onClick={() => this.copyAnswers("single")}
                          style={{
                            marginLeft: "auto",
                            border: "1px solid #16a34a",
                            background: "#fff",
                            color: "#16a34a",
                            borderRadius: 6,
                            padding: "3px 12px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          ⧉ Copy this
                        </button>
                        <button
                          type="button"
                          onClick={() => this.copyAnswers("combined")}
                          style={{
                            border: "none",
                            background: "#16a34a",
                            color: "#fff",
                            borderRadius: 6,
                            padding: "3px 12px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          ⧉ Copy all 75
                        </button>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill,minmax(52px,1fr))",
                          gap: 3,
                        }}
                      >
                        {Object.keys(rev.data.answers || {}).map((q) => {
                          const v = this.effectiveAnswer(q); // staged edit or engine read
                          // Numeric (Section-II) answers are one char per digit-column,
                          // space-separated (e.g. "0 0 0 6 0 0"); a fully-unread question comes
                          // back as all-underscores (e.g. "_ _ _ _ _ _"). For DISPLAY only:
                          // blank = unanswered, so treat the all-underscore case as empty rather
                          // than showing a run of dashes, and collapse a real value to the actual
                          // number it represents (e.g. "0 0 1 2 _ _" -> "12") via numericDisplay.
                          // `v` itself is left untouched — it still feeds the edit input.
                          const isBlankNumeric =
                            typeof v === "string" &&
                            v.includes(" ") &&
                            v.replace(/[\s_]/g, "") === "";
                          const shown = isBlankNumeric ? "" : v;
                          const display = this.numericDisplay(shown);
                          const edited = Object.prototype.hasOwnProperty.call(
                            this.state.answerEdits,
                            q,
                          );
                          const isEditing = this.state.editingQ === q;
                          const bg = edited
                            ? "#e0edff"
                            : shown === "?"
                              ? "#fdecec"
                              : shown
                                ? "#eafaef"
                                : "#f5f5f5";
                          const col =
                            shown === "?" ? "#c00" : shown ? "#127a35" : "#bbb";
                          return (
                            <div
                              key={q}
                              onClick={() =>
                                !isEditing && this.setState({ editingQ: q })
                              }
                              style={{
                                border: `1px solid ${edited ? "#3b82f6" : "#e0e0e6"}`,
                                borderRadius: 5,
                                padding: "2px 3px",
                                textAlign: "center",
                                background: bg,
                                cursor: "pointer",
                              }}
                            >
                              <div style={{ fontSize: 9, color: "#888" }}>
                                {q}
                              </div>
                              {isEditing ? (
                                <input
                                  autoFocus
                                  defaultValue={v && v !== "?" ? v : ""}
                                  onBlur={(e) => {
                                    this.setAnswerEdit(
                                      q,
                                      e.target.value.trim().toUpperCase(),
                                    );
                                    this.setState({ editingQ: null });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      this.setAnswerEdit(
                                        q,
                                        e.target.value.trim().toUpperCase(),
                                      );
                                      this.setState({ editingQ: null });
                                    }
                                    if (e.key === "Escape")
                                      this.setState({ editingQ: null });
                                  }}
                                  style={{
                                    width: "100%",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    textAlign: "center",
                                    border: "1px solid #3b82f6",
                                    borderRadius: 3,
                                    padding: 0,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 12,
                                    color: edited ? "#1d4ed8" : col,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {shown === "?" ? "?" : display || "·"}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </Col>
                  </Row>
                ) : null}

                {this.state.carouselMsg && (
                  <div
                    style={{
                      marginTop: 12,
                      fontWeight: 600,
                      color:
                        this.state.carouselMsg.startsWith("✓") ||
                        this.state.carouselMsg === "Deleted."
                          ? "#16a34a"
                          : this.state.carouselMsg.includes("failed")
                            ? "#dc2626"
                            : "#7c3aed",
                    }}
                  >
                    {this.state.carouselMsg}
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer
                style={{
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={atStart || busy}
                    onClick={() => this.carouselGo(-1)}
                  >
                    <i className="fas fa-chevron-left mr-1" />
                    Prev
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={atEnd || busy}
                    onClick={() => this.carouselGo(1)}
                  >
                    Next
                    <i className="fas fa-chevron-right ml-1" />
                  </Button>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {row &&
                    (() => {
                      const editCount = Object.keys(
                        this.state.answerEdits,
                      ).length;
                      return (
                        <>
                          {editCount > 0 && (
                            <Button
                              size="sm"
                              style={{ background: "#2563eb", border: "none" }}
                              disabled={busy}
                              onClick={this.saveAnswers}
                            >
                              <i className="fas fa-save mr-1" />
                              Save Answers ({editCount})
                            </Button>
                          )}
                          <Button
                            size="sm"
                            style={{ background: "#7c3aed", border: "none" }}
                            disabled={busy}
                            onClick={() => {
                              const r = row;
                              this.fixRoll(r).then(() =>
                                this.loadCarouselReview(),
                              );
                            }}
                          >
                            Fix Roll
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={busy}
                            onClick={() => this.pickReplace(row)}
                          >
                            <i className="fas fa-camera mr-1" />
                            Re-scan
                          </Button>
                          <Button
                            size="sm"
                            style={{ background: "#198754", border: "none" }}
                            disabled={busy || editCount > 0}
                            title={editCount > 0 ? "Save answers first" : ""}
                            onClick={this.submitOne}
                          >
                            {busy ? "…" : "Submit This"}
                          </Button>
                          <Button
                            size="sm"
                            style={{ background: "#dc3545", border: "none" }}
                            disabled={busy}
                            onClick={this.deleteOne}
                          >
                            <i className="fas fa-trash mr-1" />
                            Delete
                          </Button>
                        </>
                      );
                    })()}
                </div>
              </Modal.Footer>
            </Modal>
          );
        })()}

        {/* Add-sheets subject picker (subject-wise papers): choose the subject of the batch
            being added, then the file picker opens. */}
        <Modal show={this.state.addSubjectModal}
          onHide={() => this.setState({ addSubjectModal: false })} centered>
          <Modal.Header closeButton style={{ background: "#7c3aed" }}>
            <Modal.Title style={{ color: "#fff", fontSize: 16 }}>
              Which subject are you adding?
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: "#f8f9fa" }}>
            <div style={{ color: "#6c757d", fontSize: 13, marginBottom: 14 }}>
              These sheets are single-subject. Pick the subject so they're read and merged into
              the right part of each student's result.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {["Physics", "Chemistry", "Maths"].map((s) => (
                <button key={s} type="button"
                  onClick={() => this.setState({ addSubject: s })}
                  style={{ flex: "1 1 30%", padding: "12px 8px", borderRadius: 10, fontSize: 14,
                    fontWeight: 700, cursor: "pointer",
                    border: `2px solid ${this.state.addSubject === s ? "#7c3aed" : "#ced4da"}`,
                    background: this.state.addSubject === s ? "#f3edfd" : "#fff",
                    color: this.state.addSubject === s ? "#7c3aed" : "#495057" }}>
                  {s}
                </button>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer style={{ background: "#f8f9fa" }}>
            <Button variant="light" onClick={() => this.setState({ addSubjectModal: false })}>
              Cancel
            </Button>
            <Button style={{ background: "#7c3aed", border: "none" }}
              disabled={!this.state.addSubject} onClick={this.confirmAddSubject}>
              Choose files →
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default OmrStatusPage;
